import { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { SwitchControl } from '../Toggle/Toggle';
import { useToast } from '../Toast/Toast';
import { TeamHoldBanner } from './TeamHoldBanner';
import {
  holdTeam, resumeTeam, holdAgent, resumeAgent, useHoldStore, agentInPlan,
} from '../../team-hold/teamHold';
import { AT_HOLD_TAIL, AT_MONEY_TILE } from '../../ai-team/mockData';
import { useGmailStore } from '../../settings-gmail/store';
import type { AtAgent, AtChannelChip, AtRosterData, AtStat } from '../../ai-team/types';

export interface AiTeamRosterViewProps {
  data: AtRosterData;
  /** Open the agent's page (or Messages / Email for AI SMS / AI Email). */
  onOpen?: (to: string) => void;
  /** See everything it's sent → the Communication log, filtered, past 30 days. */
  onSeeSent?: (logKey: string) => void;
  onOpenBrain?: () => void;
  onOpenBrainChip?: (to: string) => void;
  /** Open the Dashboard on the 30-day view. */
  onOpenDashboard?: () => void;
  /** The two parts of the Waiting tile. */
  onOpenTriage?: () => void;
  onOpenCalls?: () => void;
  /** Automatic fallback banner (?fallback=1). */
  fallback?: boolean;
}

const SIGNED_IN = 'Ava Thompson';
const PRACTICE_PHONE = '(210) 555-0140';

/** The three agents that keep answering even when the whole team is held. */
const isAnswering = (a: AtAgent): boolean => a.answering === true;

function ChannelChip({ chip }: { chip: AtChannelChip }) {
  const off = chip.dir === 'not_permitted';
  const inOnly = chip.dir === 'in_only';
  return (
    <span
      aria-disabled={off || undefined}
      aria-label={off ? `${chip.label}, not used by this agent` : chip.label}
      className={cn(
        'inline-flex items-center gap-1 rounded-[var(--r-pill)] border px-2 py-0.5 text-[11px] font-semibold',
        off
          ? 'border-[var(--cream-2)] bg-transparent text-[var(--ink-4)] line-through'
          : 'border-[var(--cream-2)] bg-[var(--cream-0)] text-[var(--ink-2)]',
      )}
    >
      {chip.label}
      {inOnly ? <span className="font-normal text-[var(--ink-4)]">· in only</span> : null}
    </span>
  );
}

function StatCluster({ stats, suffix }: { stats: AtStat[]; suffix?: string | undefined }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1.5">
      {stats.map((s) => (
        <span key={s.label} className="inline-flex items-baseline gap-1.5">
          <span className={cn('font-[var(--font-mono)] font-bold tracking-[-0.01em]', s.strong ? 'text-[14px] text-[var(--ink-0)]' : 'text-[13px] text-[var(--ink-1)]')}>{s.value}</span>
          <span className="text-[11.5px] text-[var(--ink-3)]">{s.label}</span>
        </span>
      ))}
      {suffix !== undefined ? <span className="text-[11px] text-[var(--ink-4)]">{suffix}</span> : null}
    </div>
  );
}

/** Builds the full hold-modal body for an agent. */
function holdBody(agent: AtAgent): string {
  const body = agent.holdCopy.replace('{n}', agent.holdN);
  return isAnswering(agent) ? body : `${body} ${AT_HOLD_TAIL}`;
}

export function AiTeamRosterView({
  data, onOpen, onSeeSent, onOpenBrain, onOpenBrainChip, onOpenDashboard, onOpenTriage, onOpenCalls, fallback,
}: AiTeamRosterViewProps) {
  const store = useHoldStore();
  const gmail = useGmailStore().state;
  const { toast } = useToast();
  const plan = store.plan;
  const team = store.team;
  const teamHeld = team.held;

  const [holdDialog, setHoldDialog] = useState<AtAgent | null>(null);
  const [teamDialog, setTeamDialog] = useState(false);
  const [talk, setTalk] = useState<AtAgent | null>(null);
  const [talkBy, setTalkBy] = useState<'call' | 'email'>('call');

  const allAgents = data.groups.flatMap((g) => g.agents);
  const inPlan = allAgents.filter((a) => agentInPlan(a.key, plan));
  const heldOwn = inPlan.filter((a) => store.agents[a.key]?.held && !isAnswering(a));
  const workingCount = teamHeld
    ? inPlan.filter((a) => isAnswering(a)).length
    : inPlan.length - heldOwn.length;
  const planCount = inPlan.length;

  // Status line (spec 36 §3).
  const status = teamHeld
    ? 'On hold — the whole team'
    : fallback === true
      ? `${String(planCount)} of ${String(planCount)} working · backup texts only`
      : heldOwn.length > 0
        ? `${String(workingCount)} of ${String(planCount)} working · ${String(heldOwn.length)} on hold`
        : plan === 'core'
          ? `${String(planCount)} of ${String(planCount)} working · ${String(allAgents.length - planCount)} more you can add`
          : `${String(planCount)} of ${String(planCount)} working`;

  const money = plan === 'core' ? '$24,890' : AT_MONEY_TILE;
  const toCall = plan === 'core' ? 5 : 11;
  const waiting = 6 + toCall;

  const confirmHold = (agent: AtAgent) => {
    holdAgent(agent.key, { by: SIGNED_IN, since: '9:10 am', waiting: Number(agent.holdN) || 12 });
    setHoldDialog(null);
  };
  const toggleAgent = (agent: AtAgent, on: boolean) => {
    if (on) { setHoldDialog(agent); }
    else { resumeAgent(agent.key); toast({ title: `${agent.name} is working again — ${String(Number(agent.holdN) || 12)} held messages go at their next window`, variant: 'success' }); }
  };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[980px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="pc-eyebrow text-[var(--ink-3)]">{data.practiceName}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">{data.title}</h1>
          <div className="mt-0.5 text-[12.5px] text-[var(--ink-2)]">{status}</div>
        </div>
        <Button variant={teamHeld ? 'primary' : 'secondary'} size="sm" iconLeft={teamHeld ? 'play' : 'pause'}
          onClick={() => { if (teamHeld) { resumeTeam(); } else { setTeamDialog(true); } }}>
          {teamHeld ? 'Resume' : 'Hold the whole team'}
        </Button>
      </div>

      {/* held banner (shows on every agent page + Dashboard too) */}
      <div className="mt-4 empty:hidden"><TeamHoldBanner /></div>

      {fallback === true && !teamHeld ? (
        <div role="status" className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--gold)_35%,transparent)] bg-[var(--gold-tint)] px-4 py-3">
          <Icon kind="alert" size={15} stroke={2} style={{ color: 'var(--gold)' }} />
          <span className="text-[13px] font-semibold text-[var(--gold)]">Sending backup texts only</span>
        </div>
      ) : null}

      {/* three tiles */}
      <div className="mt-4 grid grid-cols-3 gap-3 max-[760px]:grid-cols-1">
        <Tile label="Working now" value={`${String(workingCount)} of ${String(planCount)}`}
          sub={!teamHeld && heldOwn.length > 0 ? `${heldOwn[0]?.name ?? ''} is on hold` : undefined} />
        <Tile label="Booked or collected · 30 days" value={money} money sub="the money on the cards below, added up" />
        <div className="rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-3.5 shadow-[var(--sh-1)]">
          <div className="font-[var(--font-mono)] text-[22px] font-bold tabular-nums text-[var(--ink-0)]">{waiting}</div>
          <div className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">Waiting on your team</div>
          <div className="mt-1 text-[11.5px]">
            <button type="button" className="font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenTriage}>6 to answer in Triage</button>
            <span className="text-[var(--ink-4)]"> · </span>
            <button type="button" className="font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenCalls}>{toCall} to call</button>
          </div>
        </div>
      </div>

      {/* groups */}
      {data.groups.map((group) => (
        <section key={group.key} className="mt-6">
          <div className="text-[13px] font-semibold text-[var(--ink-1)]">{group.title}</div>
          <div className="text-[11.5px] text-[var(--ink-3)]">{group.subtitle}</div>
          <div className="mt-2.5 flex flex-col gap-3">
            {group.agents.map((agent) => {
              const notInPlan = !agentInPlan(agent.key, plan);
              if (notInPlan) return <NotInPlanCard key={agent.key} agent={agent} onTalk={() => { setTalk(agent); setTalkBy('call'); }} />;
              // AI Email needs the practice's Gmail connected before it can work (spec 38 §4).
              if (agent.key === 'email' && gmail !== 'connected') {
                return <GmailNeededCard key={agent.key} agent={agent} expired={gmail === 'expired'} onConnect={() => { onOpen?.('/settings/ai/channels/email#gmail'); }} />;
              }
              const ownHeld = store.agents[agent.key]?.held === true;
              const held = ownHeld || (teamHeld && !isAnswering(agent));
              const aiCallsOff = agent.needsReceptionist === true && !agentInPlan('receptionist', plan);
              return (
                <AgentCard
                  key={agent.key}
                  agent={agent}
                  teamHeld={teamHeld}
                  ownHeld={ownHeld}
                  held={held}
                  aiCallsOff={aiCallsOff}
                  onOpen={() => { onOpen?.(agent.openTo); }}
                  onSeeSent={() => { onSeeSent?.(agent.logKey); }}
                  onToggle={(on) => { toggleAgent(agent, on); }}
                />
              );
            })}
          </div>
        </section>
      ))}

      <p className="mt-5 text-[11.5px] text-[var(--ink-4)]">Figures are the last 30 days, as on each agent’s page. AI Receptionist, AI SMS and AI Email show today.</p>

      {/* Brain card */}
      <section className="mt-6 rounded-[16px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[14px] font-bold text-[var(--ink-0)]">{data.brainTitle}</div>
            <div className="text-[11.5px] text-[var(--ink-3)]">{data.brainSubtitle}</div>
          </div>
          <Button variant="secondary" size="sm" onClick={onOpenBrain}>Open the Brain</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.brainChips.map((chip) => (
            <button key={chip.label} type="button" onClick={() => { onOpenBrainChip?.(chip.to); }}
              className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-2.5 py-1 text-[11.5px] text-[var(--ink-2)] hover:border-[var(--cream-shadow)]">
              <Icon kind={chip.icon} size={12} stroke={2} /> {chip.label}
            </button>
          ))}
        </div>
      </section>

      <div className="mt-4 text-right">
        <button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenDashboard}>Open the Dashboard →</button>
      </div>

      {/* per-agent hold confirm */}
      {holdDialog !== null ? (
        <Modal open title={`Hold ${holdDialog.name}?`} onClose={() => { setHoldDialog(null); }}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">{holdBody(holdDialog)}</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setHoldDialog(null); }}>Keep it working</Button>
            <Button variant="danger" size="sm" onClick={() => { confirmHold(holdDialog); }}>Hold it</Button>
          </div>
        </Modal>
      ) : null}

      {/* whole-team hold confirm */}
      {teamDialog ? (
        <Modal open title="Hold the whole team?" onClose={() => { setTeamDialog(false); }}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">Every agent stops starting conversations. Replies and post-op checks still go. The Invis-Line and AI Receptionist keep answering.</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setTeamDialog(false); }}>Keep them working</Button>
            <Button variant="danger" size="sm" onClick={() => { holdTeam({ by: SIGNED_IN, since: '10:42 am', waiting: 14, duration: 'resume' }); setTeamDialog(false); }}>Hold the team</Button>
          </div>
        </Modal>
      ) : null}

      {/* Talk to the ELVA team */}
      {talk !== null ? (
        <Modal open title="Talk to the ELVA team" onClose={() => { setTalk(null); }}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">About {talk.name}. Someone from ELVA will get in touch to go through it with you and switch it on.</p>
          <div className="mt-3">
            <div className="pc-eyebrow text-[var(--ink-3)]">Best way to reach you</div>
            <div className="mt-1.5 flex gap-2">
              {(['call', 'email'] as const).map((w) => (
                <button key={w} type="button" onClick={() => { setTalkBy(w); }}
                  className={cn('rounded-[var(--r-md)] border px-3 py-1.5 text-[12.5px] font-semibold', w === talkBy ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'border-[var(--cream-2)] text-[var(--ink-2)]')}>
                  {w === 'call' ? 'Call me' : 'Email me'}
                </button>
              ))}
            </div>
            <div className="mt-1.5 text-[11.5px] text-[var(--ink-3)]">{SIGNED_IN} · {PRACTICE_PHONE}</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setTalk(null); }}>Not now</Button>
            <Button variant="primary" size="sm" onClick={() => { const verb = talkBy === 'call' ? 'call' : 'email'; toast({ title: `Sent — the ELVA team will ${verb} ${SIGNED_IN} today`, variant: 'success' }); setTalk(null); }}>Send</Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function Tile({ label, value, sub, money }: { label: string; value: string; sub?: string | undefined; money?: boolean }) {
  return (
    <div className="rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-3.5 shadow-[var(--sh-1)]">
      <div className={cn('font-[var(--font-mono)] text-[22px] font-bold tabular-nums', money === true ? 'text-[var(--money-green)]' : 'text-[var(--ink-0)]')}>{value}</div>
      <div className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">{label}</div>
      {sub !== undefined ? <div className="mt-0.5 text-[11px] text-[var(--ink-4)]">{sub}</div> : null}
    </div>
  );
}

function AgentCard({ agent, teamHeld, ownHeld, held, aiCallsOff, onOpen, onSeeSent, onToggle }: {
  agent: AtAgent; teamHeld: boolean; ownHeld: boolean; held: boolean; aiCallsOff: boolean;
  onOpen: () => void; onSeeSent: () => void; onToggle: (on: boolean) => void;
}) {
  const answering = isAnswering(agent);
  const teamLocked = teamHeld && !answering;
  const pill = teamHeld
    ? (answering ? { label: 'Still answering', fg: 'var(--success)', bg: 'var(--success-tint)' } : { label: 'On hold · whole team', fg: '#9A6B1E', bg: 'var(--gold-tint)' })
    : (ownHeld ? { label: 'On hold', fg: '#9A6B1E', bg: 'var(--gold-tint)' } : { label: 'Working', fg: 'var(--success)', bg: 'var(--success-tint)' });
  const suffix = agent.key === 'invisalign' ? 'right now' : undefined;

  return (
    <div className="rounded-[16px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
      <div className="flex flex-wrap items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]"><Icon kind={agent.icon} size={16} stroke={2} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="text-[14px] font-semibold text-[var(--ink-0)] hover:underline" onClick={onOpen}>{agent.name}</button>
            <span className="rounded-[var(--r-pill)] px-2 py-0.5 text-[10.5px] font-semibold" style={{ color: pill.fg, background: pill.bg }}>{pill.label}</span>
            <span className="rounded-[var(--r-pill)] border border-[var(--cream-2)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">{agent.planTag}</span>
            {agent.tag !== undefined ? <span className="text-[11px] text-[var(--ink-4)]">{agent.tag}</span> : null}
          </div>
          {agent.line !== undefined ? <div className="mt-1 text-[12px] text-[var(--ink-2)]">{agent.line}</div> : <div className="mt-1 text-[12px] text-[var(--ink-2)]">{agent.oneLine}</div>}
          <div className="mt-2"><StatCluster stats={agent.stats} suffix={suffix} /></div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {agent.channels?.map((c) => (
              <ChannelChip key={c.label} chip={aiCallsOff && c.label === 'Call' ? { label: 'Call', dir: 'not_permitted' } : c} />
            ))}
          </div>
          {aiCallsOff ? (
            <div className="mt-1.5 text-[11px] text-[var(--ink-4)]">AI calls need the AI Receptionist — AI-call steps are skipped; the next step goes as usual.</div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1">
          <SwitchControl
            checked={!held}
            disabled={teamLocked}
            size="sm"
            ariaLabel={`${agent.name}, ${held ? 'on hold' : 'working'}`}
            {...(teamLocked ? {} : { onChange: (next: boolean) => { onToggle(!next); } })}
          />
          {teamLocked ? <span className="text-[9.5px] text-[var(--ink-4)]" title="The whole team is on hold — use Resume at the top">whole team</span> : null}
        </div>
      </div>

      {ownHeld && !teamHeld ? (
        <div role="status" className="mt-3 rounded-[10px] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
          On hold since {agent.holdN === '—' ? '9:10 am' : '9:10 am'} by {SIGNED_IN} · {Number(agent.holdN) || 12} messages waiting · replies are still answered. Switch it on to resume.
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[var(--cream-2)] pt-2.5">
        <Button variant="secondary" size="sm" onClick={onOpen}>{agent.openLabel ?? 'Open'}</Button>
        <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onSeeSent}>See everything it’s {agent.seenLabel}</button>
      </div>
    </div>
  );
}

function GmailNeededCard({ agent, expired, onConnect }: { agent: AtAgent; expired: boolean; onConnect: () => void }) {
  const pill = expired
    ? { label: 'Needs reconnecting', fg: 'var(--alert-red)', bg: '#FBEDEC' }
    : { label: 'Needs Gmail', fg: '#9A6B1E', bg: 'var(--gold-tint)' };
  return (
    <div className="rounded-[16px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
      <div className="flex flex-wrap items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]"><Icon kind={agent.icon} size={16} stroke={2} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-[var(--ink-0)]">{agent.name}</span>
            <span className="rounded-[var(--r-pill)] px-2 py-0.5 text-[10.5px] font-semibold" style={{ color: pill.fg, background: pill.bg }}>{pill.label}</span>
            <span className="rounded-[var(--r-pill)] border border-[var(--cream-2)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">{agent.planTag}</span>
          </div>
          <div className="mt-1 text-[12px] text-[var(--ink-2)]">{agent.oneLine}</div>
          <div className="mt-2 text-[12px] font-semibold text-[var(--ink-3)]">
            <SwitchControl checked={false} disabled size="sm" ariaLabel={`${agent.name}, needs Gmail`} />
            <span className="ml-2 align-middle">Connect the practice’s Gmail to switch it on</span>
          </div>
        </div>
      </div>
      <div className="mt-3 border-t border-[var(--cream-2)] pt-2.5">
        <Button variant="secondary" size="sm" iconLeft="mail" onClick={onConnect}>Connect Gmail</Button>
      </div>
    </div>
  );
}

function NotInPlanCard({ agent, onTalk }: { agent: AtAgent; onTalk: () => void }) {
  return (
    <div className="rounded-[16px] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)] p-4 opacity-90">
      <div className="flex flex-wrap items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-[var(--cream-1)] text-[var(--ink-4)]"><Icon kind={agent.icon} size={16} stroke={2} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-[var(--ink-2)]">{agent.name}</span>
            <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--cream-1)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--ink-3)]"><Icon kind="lock" size={10} stroke={2} /> Not in your plan</span>
            <span className="rounded-[var(--r-pill)] border border-[var(--cream-2)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">{agent.planTag}</span>
          </div>
          <div className="mt-1 text-[12px] text-[var(--ink-2)]">{agent.oneLine}</div>
          <div className="mt-1 text-[11.5px] text-[var(--ink-3)]">{agent.whatItAdds}</div>
        </div>
        <Icon kind="lock" size={16} stroke={2} style={{ color: 'var(--ink-4)' }} />
      </div>
      <div className="mt-3 border-t border-[var(--cream-2)] pt-2.5">
        <Button variant="secondary" size="sm" onClick={onTalk}>Talk to the ELVA team</Button>
      </div>
    </div>
  );
}
